import { FC } from 'react';
import { Box, FormControlLabel, Switch } from '@mui/material';
import SickDaysSection from './List/SickDaysSection';
import OtherLeavesSection from './List/OtherLeavesSection';
import PaidLeavesSection from './List/PaidLeavesSection';
import useLeaveBalances from '../shared/hooks/useLeaveBalances';
import EmployeeSectionWrapper from 'domain/people/employee/shared/EmployeeSectionWrapper';
import { Loading } from 'framework/ui/mui-builder';
import { ProcessedLeaveYear } from '../shared/types';
import { isCurrentOrNextPeriod } from '../shared/leaveBalanceUtils';

interface LeaveBalancesContainerProps {
  id: string;
  hideShowDetailsButtons?: boolean;
}

const LeaveBalances: FC<LeaveBalancesContainerProps> = ({ id, hideShowDetailsButtons = false }) => {
  const {
    showAllPeriods,
    setShowAllPeriods,
    currentPeriod,
    nextPeriod,
    isPolishContractor,
    paidLeavesItems,
    sickDaysItems,
    getPaidLeavesDataMethod,
    getSickDaysDataMethod,
    getOtherLeavesDataMethod,
    otherLeavesDefaultFilter,
    isDataReady,
  } = useLeaveBalances(id);

  const currentOrNextPeriodCallback = (data: ProcessedLeaveYear) => {
    if (showAllPeriods || !currentPeriod) {
      return false;
    }
    return isCurrentOrNextPeriod(data, currentPeriod, nextPeriod);
  };

  const shouldShowPaidLeavesSection = !isPolishContractor || paidLeavesItems.length > 0;
  const shouldShowSickDaysSection = !isPolishContractor || sickDaysItems.length > 0;
  const isPaidLeavesCompactMode = paidLeavesItems.length > 0 && paidLeavesItems.every(item => item.hideBalanceColumns);
  const isSickDaysCompactMode = sickDaysItems.length > 0 && sickDaysItems.every(item => item.hideBalanceColumns);

  return !isDataReady ? (
    <EmployeeSectionWrapper withMinHeight={false}>
      <Loading isInitialPosition />
    </EmployeeSectionWrapper>
  ) : (
    <>
      <Box py={1} px={2} mb={2} sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}>
        <FormControlLabel
          control={
            <Switch checked={showAllPeriods} onChange={e => setShowAllPeriods(e.target.checked)} color="primary" />
          }
          label="Show all periods"
        />
      </Box>

      {shouldShowPaidLeavesSection && (
        <PaidLeavesSection
          key={`paid-${showAllPeriods}`}
          getPaidLeavesDataMethod={getPaidLeavesDataMethod}
          compactMode={isPaidLeavesCompactMode}
          defaultExpandedItems={currentOrNextPeriodCallback}
          hideShowDetailsButtons={hideShowDetailsButtons}
        />
      )}

      {shouldShowSickDaysSection && (
        <SickDaysSection
          key={`sick-${showAllPeriods}`}
          getSickDaysDataMethod={getSickDaysDataMethod}
          compactMode={isSickDaysCompactMode}
          defaultExpandedItems={currentOrNextPeriodCallback}
          hideShowDetailsButtons={hideShowDetailsButtons}
        />
      )}

      <OtherLeavesSection
        key={`other-${showAllPeriods}-${currentPeriod?.startDate || 'no-current-period'}`}
        getOtherLeavesDataMethod={getOtherLeavesDataMethod}
        defaultFilter={otherLeavesDefaultFilter}
        showAllPeriods={showAllPeriods}
        isPolishContractor={isPolishContractor}
        hideShowDetailsButtons={hideShowDetailsButtons}
      />
    </>
  );
};

export default LeaveBalances;
